import { ChevronUpIcon } from '@heroicons/react/24/outline';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';

/**
 * アコーディオンアイテムの設定
 */
type AccordionItemConfig = {
  /** アイテムの一意なキー（value属性に使用） */
  key: string;
  /** トリガー部分に表示するコンテンツ */
  trigger: ReactNode;
  /** 展開時に表示するコンテンツ */
  content: ReactNode;
  /** 無効状態（編集中など） */
  disabled?: boolean;
  /** アイテムに適用する追加クラス */
  className?: string;
  /** バリアント */
  variant?: 'default' | 'highlighted';
};

/**
 * 単一選択モードのProps
 */
type SingleAccordionProps = {
  type: 'single';
  /** 現在の開いているアイテムのキー */
  activeKey?: string;
  /** デフォルトで開くアイテムのキー */
  defaultActiveKey?: string;
  /** アイテムの開閉状態が変わったときのコールバック */
  onActiveKeyChange?: (activeKey: string) => void;
  /** すべて閉じることを許可するか */
  collapsible?: boolean;
};

/**
 * 複数選択モードのProps
 */
type MultipleAccordionProps = {
  type: 'multiple';
  /** 現在開いているアイテムのキー配列 */
  activeKey?: string[];
  /** デフォルトで開くアイテムのキー配列 */
  defaultActiveKey?: string[];
  /** アイテムの開閉状態が変わったときのコールバック */
  onActiveKeyChange?: (activeKey: string[]) => void;
};

type AccordionProps = {
  /** アコーディオンアイテムの配列 */
  items: AccordionItemConfig[];
  /** ルート要素に適用する追加クラス */
  wrapperClassName?: string;
} & (SingleAccordionProps | MultipleAccordionProps);

/**
 * アコーディオンコンポーネント
 *
 * 折りたたみ可能なコンテンツセクションを提供します。
 * Tab.tsx と同様に items 配列を受け取り、内部で組み立てます。
 *
 * @see Figma: https://www.figma.com/design/K9BryoBh0t69xPihm3VBho/【DnR】DesignSystem?node-id=5058-18331
 * @see Radix UI: https://www.radix-ui.com/primitives/docs/components/accordion
 *
 * @example
 * ```tsx
 * // 単一選択モード
 * <Accordion
 *   type="single"
 *   collapsible
 *   items={[
 *     {
 *       key: 'section1',
 *       trigger: <Text variant="label">セクション1</Text>,
 *       content: <p>コンテンツ1</p>,
 *     },
 *     {
 *       key: 'section2',
 *       trigger: <Text variant="label">セクション2</Text>,
 *       content: <p>コンテンツ2</p>,
 *     },
 *   ]}
 * />
 *
 * // 制御モード
 * <Accordion
 *   type="single"
 *   collapsible
 *   activeKey={activeKey}
 *   onActiveKeyChange={setActiveKey}
 *   items={[...]}
 * />
 * ```
 */

/**
 * アコーディオントリガーのバリアント
 *
 * Figmaデザインシステム: WVAccordionTrigger
 * - 高さ: 56px（デフォルト）, 40px（sm）
 * - パディング: 12px
 * - 角丸: 8px
 * - フォーカス: リング表示
 */

export const Accordion = (props: AccordionProps) => {
  const { items, wrapperClassName } = props;

  if (items.length === 0) {
    return null;
  }

  const renderItems = () => {
    return items.map((item) => {
      return (
        <AccordionPrimitive.Item
          key={item.key}
          value={item.key}
          className={cn(
            'border-border-base border-b',
            item.variant === 'highlighted' && 'data-[state=open]:bg-surface-strength1',
            item.className,
          )}
        >
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger
              disabled={item.disabled}
              className={cn(
                'flex w-full flex-1 cursor-pointer items-center gap-2 p-4 transition-all',
                '[&[data-state=open]>svg]:rotate-180',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                item.disabled && 'cursor-not-allowed opacity-70',
              )}
            >
              <ChevronUpIcon className="size-4 shrink-0 text-text-secondary transition-transform duration-200" />
              {item.trigger}
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionPrimitive.Content className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
            <div className="pt-0 pr-4 pb-4 pl-10">{item.content}</div>
          </AccordionPrimitive.Content>
        </AccordionPrimitive.Item>
      );
    });
  };

  // Radix UI の props にマッピング（type によって分岐）
  if (props.type === 'single') {
    return (
      <AccordionPrimitive.Root
        type="single"
        className={wrapperClassName}
        value={props.activeKey}
        defaultValue={props.defaultActiveKey}
        onValueChange={props.onActiveKeyChange}
        collapsible={props.collapsible}
      >
        {renderItems()}
      </AccordionPrimitive.Root>
    );
  }

  return (
    <AccordionPrimitive.Root
      type="multiple"
      className={wrapperClassName}
      value={props.activeKey}
      defaultValue={props.defaultActiveKey}
      onValueChange={props.onActiveKeyChange}
    >
      {renderItems()}
    </AccordionPrimitive.Root>
  );
};
